import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../constant";
import { FaCheckCircle, FaExclamationCircle, FaSearch, FaEnvelope, FaEye, FaTimes } from "react-icons/fa";
import '../MiniAgentReferralHistoty/MiniAgentReferralHistory.css';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addressFilter, setAddressFilter] = useState("all");
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [processingId, setProcessingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("account_admin_token");
      const params = new URLSearchParams({
        per_page: "10",
        page: currentPage.toString(),
      });
      if (search) params.append("search", search);
      if (addressFilter !== "all") params.append("address_status", addressFilter);

      const response = await fetch(
        `${API_URL}/api/account-admin/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setPagination(data.pagination || {});
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, addressFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    setCurrentPage(1);
  }, [search, addressFilter]);

  const handleRequestAddress = async (userId) => {
    setProcessingId(userId);
    setMessage({ text: "", type: "" });

    try {
      const token = localStorage.getItem("account_admin_token");
      const response = await fetch(
        `${API_URL}/api/account-admin/users/request-address`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ customer_user_id: userId }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setMessage({
          text: data.message || "Address request sent successfully!",
          type: "success",
        });
        fetchUsers(); // Refresh list
      } else {
        setMessage({
          text: data.message || "Failed to send address request.",
          type: "error",
        });
      }
    } catch (err) {
      setMessage({ text: "An error occurred.", type: "error" });
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      const token = localStorage.getItem("account_admin_token");
      const response = await fetch(
        `${API_URL}/api/account-admin/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data.user);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error("Failed to load user details:", err);
    }
  };

  const totalPages = pagination.last_page || 1;

  return (
    <div className="customer-referral-history-container">


      {/* Message Banner */}
      {message.text && (
        <div
          style={{
            padding: "15px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor:
              message.type === "error" ? "#fdecea" : "#e8f5e9",
            color: message.type === "error" ? "#d32f2f" : "#2e7d32",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          }}
        >
          {message.type === "error" ? (
            <FaExclamationCircle />
          ) : (
            <FaCheckCircle />
          )}
          {message.text}
          <button
            onClick={() => setMessage({ text: "", type: "" })}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.1rem",
              color: "inherit",
            }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: "transparent", borderRadius: "12px", marginBottom: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.1)" }}>
        <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", gap: "10px" }}>
          <input
            type="text"
            placeholder="Search by Customer Name, Email, or Phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #ccc" }}
           maxLength={20} />
          <button type="submit" style={{ padding: "10px 20px", background: "#001964", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" }}>
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", padding: "8px" }}>
        {loading ? (
          <div className="loading-container" style={{ margin: "40px auto" }}>
            <div
              className="loading-spinner"
              style={{
                borderColor: "#001964 transparent #001964 transparent",
              }}
            ></div>
            <p style={{ marginTop: "10px", color: "#333" }}>
              Loading users...
            </p>
          </div>
        ) : users.length === 0 ? (
          <div
            className="empty-state"
            style={{ margin: "40px auto", color: "#333" }}
          >
            <div className="empty-icon">👥</div>
            <h3 style={{ color: "#333" }}>No Users Found</h3>
            <p style={{ color: "#666" }}>
              {search || addressFilter !== "all"
                ? "Try adjusting your search or filters."
                : "There are no customers in the system yet."}
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th style={{ textAlign: "center" }}>Address Status</th>
                    <th>City</th>
                    <th>Postcode</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id}>
                      <td className="name-cell">
                        {user.first_name} {user.last_name}
                      </td>
                      <td style={{ fontSize: "0.9rem" }}>{user.email}</td>
                      <td style={{ fontSize: "0.9rem" }}>
                        {user.phone_number || "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {user.has_full_address ? (
                          <span
                            className="status-badge active"
                            style={{
                              display: "inline-block",
                              background: "#e8f5e9",
                              color: "#2e7d32",
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "0.8rem",
                              fontWeight: "600",
                            }}
                          >
                            ✓ Complete
                          </span>
                        ) : (
                          <span
                            className="status-badge pending"
                            style={{
                              display: "inline-block",
                              background: "#fdecea",
                              color: "#d32f2f",
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "0.8rem",
                              fontWeight: "600",
                            }}
                          >
                            ✗ Incomplete
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: "0.9rem" }}>
                        {user.city || (
                          <span style={{ color: "#ccc" }}>—</span>
                        )}
                      </td>
                      <td style={{ fontSize: "0.9rem" }}>
                        {user.postcode || (
                          <span style={{ color: "#ccc" }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
            
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            onClick={() => handleViewDetails(user.user_id)}
                            title="View Details"
                            style={{
                              background: "#001964",
                              color: "#fff",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FaEye /> View
                          </button>
                          <button
                            onClick={() =>
                              !user.has_full_address && handleRequestAddress(user.user_id)
                            }
                            disabled={processingId === user.user_id || user.has_full_address}
                            title="Request customer to provide their address"
                            style={{
                              visibility: user.has_full_address ? "hidden" : "visible",
                              background: user.address_requested_at
                                ? "#ff9800"
                                : "#d32f2f",
                              color: "#fff",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              cursor: user.has_full_address ? "default" : "pointer",
                              fontSize: "0.85rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              opacity:
                                processingId === user.user_id ? 0.7 : 1,
                            }}
                            className="desktop-request-address-btn"
                          >
                            <FaEnvelope />
                            {processingId === user.user_id
                              ? "Sending..."
                              : user.address_requested_at
                              ? "Re-send"
                              : "Request Address"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container" style={{ marginTop: '20px' }}>
                <div className="pagination">
                  <button 
                    className="page-btn prev-next" 
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1}
                  >
                    ←
                  </button>
                  
                  <div className="page-numbers">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={i}
                          className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button 
                    className="page-btn prev-next" 
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    →
                  </button>
                </div>
                
                <div className="page-info">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "30px",
              maxWidth: "550px",
              width: "100%",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, color: "#333", fontSize: "1.4rem" }}>
                Customer Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#999",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              <DetailRow label="Name" value={`${selectedUser.first_name} ${selectedUser.last_name}`} />
              <DetailRow label="Email" value={selectedUser.email} />
              <DetailRow label="Phone" value={selectedUser.phone_number || "—"} />
              <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "5px 0" }} />
              <DetailRow label="Address Line 1" value={selectedUser.address_line1} missing={!selectedUser.address_line1} />
              <DetailRow label="Address Line 2" value={selectedUser.address_line2 || "—"} />
              <DetailRow label="Address Line 3" value={selectedUser.address_line3 || "—"} />
              <DetailRow label="City" value={selectedUser.city} missing={!selectedUser.city} />
              <DetailRow label="Postcode" value={selectedUser.postcode} missing={!selectedUser.postcode} />
              <DetailRow label="Country" value={selectedUser.country || "—"} />
              <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "5px 0" }} />
              <DetailRow
                label="Address Status"
                value={
                  selectedUser.has_full_address ? (
                    <span style={{ color: "#2e7d32", fontWeight: "600" }}>✓ Complete</span>
                  ) : (
                    <span style={{ color: "#d32f2f", fontWeight: "600" }}>✗ Incomplete</span>
                  )
                }
              />
              {selectedUser.address_requested_at && (
                <DetailRow
                  label="Address Requested"
                  value={new Date(selectedUser.address_requested_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                />
              )}
              {!selectedUser.has_full_address && selectedUser.missing_fields?.length > 0 && (
                <DetailRow
                  label="Missing Fields"
                  value={selectedUser.missing_fields.map(f =>
                    f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                  ).join(", ")}
                />
              )}
            </div>

            {!selectedUser.has_full_address && (
              <button
                onClick={() => {
                  handleRequestAddress(selectedUser.user_id);
                  setShowDetailsModal(false);
                }}
                style={{
                  width: "100%",
                  marginTop: "20px",
                  padding: "12px",
                  background: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <FaEnvelope /> Request Address via Email
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, missing }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span style={{ fontWeight: "600", color: "#555", fontSize: "0.9rem" }}>{label}</span>
    <span
      style={{
        color: missing ? "#d32f2f" : "#333",
        fontSize: "0.9rem",
        fontStyle: missing ? "italic" : "normal",
      }}
    >
      {missing ? "Not provided" : value}
    </span>
  </div>
);

export default ManageUsers;
